import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggingLevelEnum } from '@auth/auth';
import { map, Observable, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { UserAttributeIdentifierEnum } from '../enums/enums';
import { EventType } from '../enums/enums/event-type';
import { CallSite } from '../types/callsite.types';
import { LogRequest, LogResponse } from '../types/usereco-api.types';
import { EnvironmentService } from './environment/environment.service';

const ErrorCallSites: (err: Error) => CallSite[] = require('error-callsites');

@Injectable({
  providedIn: 'root'
})
export class UserecoApiService {
  // Store the maintenance mode value for the duration of the session
  private isMaintenanceModeCache: Record<number, boolean> = {};

  /**
   * Should be accessed via getSessionId
   */
  private _sessionId: string | undefined;

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  getMaintenanceMode(appPath: string | undefined): Observable<boolean> {
    const appId = this.mapAppId(appPath);

    if (this.isMaintenanceModeCache[appId] !== undefined) {
      return of(this.isMaintenanceModeCache[appId]);
    }

    const url = this.getFullUrl(`applicationSettings/maintenanceMode/${appId}`);
    return this.http.get<{ isMaintenanceModeEnabled: boolean }>(url).pipe(
      map((response) => {
        const isMaintenanceModeEnabled = response.isMaintenanceModeEnabled;
        this.isMaintenanceModeCache[appId] = isMaintenanceModeEnabled;
        return isMaintenanceModeEnabled;
      })
    );
  }

  log(request: LogRequest): Observable<LogResponse> {
    const url = this.getFullUrl('log');
    return this.http.post<LogResponse>(url, request);
  }

  createLogRequest(
    eventType: EventType,
    message: string,
    eventData?: any,
    error?: Error,
    applicationID: number = 1
  ): LogRequest {
    const callsite = UserecoApiService.getCallSite(error);

    if (eventType == EventType.Successful_Login) {
      eventData = {
        ...(eventData || {}),
        viewPortWidth: window.innerWidth
      };
    }

    const request: LogRequest = {
      applicationID,
      loggingUnit: this.getSessionId(),
      topLevelName: `SHELL-${LoggingLevelEnum.EVENT}`,
      middleLevelName: callsite.getTypeName() ?? 'Unknown Class',
      bottomLevelName: callsite.getMethodName() ?? 'Unknown Method',
      message: message,
      machine: 'User Machine',
      eventTypeId: eventType,
      eventData: eventData ? JSON.stringify(eventData) : null,
      pageTitle: 'Broadspire Platform',
      applicationPath: window.location.href.substring(0, 5000),
      fileName: callsite.getFileName(),
      lineNumber: callsite.getLineNumber() || 0,
      userAttributes: [
        UserAttributeIdentifierEnum.USER,
        UserAttributeIdentifierEnum.CCP
      ]
    };

    return request;
  }

  getSessionId(): string {
    if (!this._sessionId) {
      this._sessionId = uuidv4();
    }

    return this._sessionId;
  }

  private getFullUrl(path: string): string {
    const baseUrl = this.environmentService.get('apiUrl')?.replace(/\/$/, '');
    return `${baseUrl}/${path}`;
  }

  private mapAppId(appPath: string | undefined): number {
    switch (appPath) {
      case 'claim-lookup':
        return 2;
      case 'analytics':
        return 3;
      case 'reports':
        return 4;
      default:
        // fallback to shell
        return 1;
    }
  }

  private static getCallSite(original?: Error): CallSite {
    if (original) {
      const callsites = ErrorCallSites(original);
      if (callsites) {
        return callsites[0];
      }
    }

    return {
      getTypeName: () => 'Unknown Class',
      getMethodName: () => 'Unknown Method',
      getFileName: () => 'Unknown File',
      getLineNumber: () => 0,
      getColumnNumber: () => 0,
      getFunctionName: () => 'Unknown Source'
    };
  }
}
